/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Landmark, Mail, ExternalLink, Check, Clock, X, ShieldCheck, ArrowRight, Copy } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

/**
 * Full-screen blocking gate shown to clients on first login when the firm
 * has flagged them as a Prosaic or Xero user. The gate dismisses automatically
 * once the client has confirmed connection (or skips for 24 hours).
 */
export default function DataSourceGate({ children }) {
    const { user } = useAuth();
    const [state, setState] = useState(null);   // server response or null while loading
    const [loaded, setLoaded] = useState(false);

    async function refresh() {
        try {
            const { data } = await api.get("/portal/onboarding/");
            setState(data);
        } catch {
            setState({ gate_required: false });
        } finally {
            setLoaded(true);
        }
    }

    useEffect(() => {
        // Only clients see the gate
        if (user?.role !== "client") {
            setState({ gate_required: false });
            setLoaded(true);
            return;
        }
        refresh();
    }, [user?.id, user?.role]);

    if (!loaded) {
        return (
            <div className="min-h-screen grid place-items-center text-[#5E636E]" data-testid="portal-loading">
                <Loader2 className="h-5 w-5 animate-spin" />
            </div>
        );
    }
    if (!state?.gate_required) return children;
    return <GateScreen state={state} onChange={refresh} />;
}

function GateScreen({ state, onChange }) {
    const { data_source, prosaic_url, xero_email, prosaic_confirmed_at, xero_confirmed_at } = state;
    const showProsaic = data_source === "prosaic" || data_source === "both";
    const showXero = data_source === "xero" || data_source === "both";

    return (
        <div className="min-h-[100dvh] bg-[#F5F4EF] px-4 py-8 sm:py-14" data-testid="data-source-gate">
            <div className="mx-auto max-w-2xl">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 rounded-full bg-[#0E3B2E]/10 grid place-items-center">
                        <ShieldCheck className="h-6 w-6 text-[#0E3B2E]" />
                    </div>
                    <div className="co-label mt-4">One quick step</div>
                    <h1 className="font-display text-2xl sm:text-3xl mt-1 font-medium">Connect your numbers</h1>
                    <p className="mt-3 text-sm text-[#5E636E] leading-relaxed max-w-md mx-auto">
                        Welcome, {state.client_name}. Before we get started, please link us to your accounting data so we can keep on top of GST, tax and bookkeeping without chasing you for paperwork.
                    </p>
                </div>

                <div className={`mt-8 grid gap-4 ${showProsaic && showXero ? "sm:grid-cols-2" : ""}`}>
                    {showProsaic && (
                        <ProsaicCard url={prosaic_url} confirmedAt={prosaic_confirmed_at} onChange={onChange} />
                    )}
                    {showXero && (
                        <XeroCard advisorEmail={xero_email} confirmedAt={xero_confirmed_at} onChange={onChange} />
                    )}
                </div>

                <div className="mt-8 flex flex-col items-center gap-2">
                    <button
                        type="button"
                        onClick={async () => {
                            try { await api.post("/portal/onboarding/skip"); toast.success("We'll remind you tomorrow"); await onChange(); }
                            catch (e) { toast.error(formatApiError(e)); }
                        }}
                        className="text-xs text-[#5E636E] hover:text-[#0F1115] inline-flex items-center gap-1"
                        data-testid="gate-skip"
                    >
                        <Clock className="h-3 w-3" /> Skip for 24 hours
                    </button>
                    <p className="text-[10px] text-[#A4A6AB] max-w-md text-center">
                        We&apos;ll never see your banking login. Prosaic gives us read-only transaction data only. Xero only gives us the role you grant.
                    </p>
                </div>
            </div>
        </div>
    );
}

function ProsaicCard({ url, confirmedAt, onChange }) {
    const [busy, setBusy] = useState(false);
    async function confirm() {
        setBusy(true);
        try {
            await api.post("/portal/onboarding/confirm", { source: "prosaic" });
            toast.success("Thanks — we'll let you know when the connection appears.");
            await onChange();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    const done = !!confirmedAt;
    return (
        <div className={`rounded-2xl border-2 bg-white p-5 ${done ? "border-emerald-300" : "border-[#0E3B2E]/20"}`} data-testid="gate-prosaic">
            <div className="flex items-start justify-between">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
                    <Landmark className="h-5 w-5" />
                </div>
                {done && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700"><Check className="h-3.5 w-3.5" /> Confirmed</span>}
            </div>
            <div className="mt-3 font-display text-lg font-medium">Connect via Prosaic</div>
            <p className="mt-1.5 text-xs text-[#5E636E] leading-relaxed">
                Prosaic is a NZ-built bank-feed service. Tap below, sign in with your bank, give us read-only access — done in 2 minutes.
            </p>
            <div className="mt-4 space-y-2">
                <Button
                    asChild
                    disabled={!url || done}
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="gate-prosaic-open"
                >
                    <a href={url || "#"} target="_blank" rel="noreferrer noopener">
                        <ExternalLink className="h-4 w-4" /> {url ? "Open Prosaic" : "Awaiting firm setup"}
                    </a>
                </Button>
                {!done && (
                    <Button variant="outline" disabled={busy} onClick={confirm} className="w-full" data-testid="gate-prosaic-confirm">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> I&apos;ve connected my bank</>}
                    </Button>
                )}
            </div>
        </div>
    );
}

function XeroCard({ advisorEmail, confirmedAt, onChange }) {
    const [busy, setBusy] = useState(false);
    async function confirm() {
        setBusy(true);
        try {
            await api.post("/portal/onboarding/confirm", { source: "xero" });
            toast.success("Thanks — we'll let you know when the invite arrives.");
            await onChange();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    function copyEmail() {
        if (!advisorEmail) return;
        navigator.clipboard?.writeText(advisorEmail);
        toast.success("Copied");
    }
    const done = !!confirmedAt;
    return (
        <div className={`rounded-2xl border-2 bg-white p-5 ${done ? "border-emerald-300" : "border-sky-200"}`} data-testid="gate-xero">
            <div className="flex items-start justify-between">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-sky-50 text-sky-700">
                    <Mail className="h-5 w-5" />
                </div>
                {done && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700"><Check className="h-3.5 w-3.5" /> Confirmed</span>}
            </div>
            <div className="mt-3 font-display text-lg font-medium">Invite us into Xero</div>
            <p className="mt-1.5 text-xs text-[#5E636E] leading-relaxed">
                In Xero, go to <span className="font-medium text-[#0F1115]">Settings → Users → Invite a user</span> and add this email as an Adviser:
            </p>
            {advisorEmail ? (
                <button
                    type="button"
                    onClick={copyEmail}
                    className="mt-3 flex items-center gap-2 w-full rounded-md bg-sky-50 border border-sky-200 px-3 py-2 text-sm font-mono hover:bg-sky-100"
                    data-testid="gate-xero-email"
                >
                    <span className="flex-1 truncate text-left">{advisorEmail}</span>
                    <Copy className="h-3.5 w-3.5 text-sky-700" />
                </button>
            ) : (
                <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                    Your accountant hasn&apos;t set the advisor email yet. Skip for 24 hours and check back.
                </div>
            )}
            <ol className="mt-3 ml-4 list-decimal space-y-0.5 text-[11px] text-[#5E636E] leading-snug">
                <li>Open <a href="https://login.xero.com" target="_blank" rel="noreferrer noopener" className="underline">login.xero.com</a></li>
                <li>Settings → Users → Invite a user</li>
                <li>Paste the email above, role <span className="font-medium">Adviser</span>, send invite</li>
            </ol>
            <div className="mt-4">
                {!done ? (
                    <Button onClick={confirm} disabled={busy || !advisorEmail} className="w-full bg-sky-700 hover:bg-sky-800 text-white" data-testid="gate-xero-confirm">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> I&apos;ve sent the invite</>}
                    </Button>
                ) : (
                    <div className="text-[11px] text-emerald-700 text-center">We&apos;ll mark this verified once your invite arrives.</div>
                )}
            </div>
        </div>
    );
}
